import { PDFDocument, rgb } from "pdf-lib"
import fontkit from "@pdf-lib/fontkit"
import robotoMed from "../../../fonts/Roboto/Roboto-Medium.ttf"
import robotoReg from "../../../fonts/Roboto/Roboto-Regular.ttf"
import robotoIta from "../../../fonts/Roboto/Roboto-MediumItalic.ttf"
import robotoItalicLight from "../../../fonts/Roboto/Roboto-LightItalic.ttf"
import { storage } from "../../../firebase/config"
import { getDownloadURL, ref, uploadBytes } from "firebase/storage"
import { calculateTextBlockHeight } from "./functions/calculateTextBlockHeight"
import { drawNewProductTableHeader } from "./functions/drawNewProductTableHeader"
import { numberToWords } from "../Create/NumbersToWords"

function drawPageNumber(
  page,
  pageNumber,
  fontSize,
  font,
  paymentText,
  docNumber,
  boldFont,
  italicFont
) {
  // Draw the page number text at specified positions
  page.drawText(`${pageNumber}`, {
    x: 570, // You might adjust this value
    y: 20, // Adjust according to where you want the number to appear
    size: fontSize,
    font: font,
    color: rgb(0, 0, 0),
  })

  page.drawLine({
    start: { x: 15, y: 35 },
    end: { x: 580, y: 35 },
    thickness: 1,
    color: rgb(0, 0, 0),
  })

  page.drawText(`${paymentText + " " + docNumber}`, {
    x: 20, // You might adjust this value
    y: 20, // Adjust according to where you want the number to appear
    size: fontSize,
    font: italicFont,
    color: rgb(0, 0, 0),
  })
}

//max text width for receiver data
const maxTextWidth = 250 // Define the maximum width for the text

// Dynamic function to draw text with wrapping and label-value alignment
function drawTextWithLabel(
  page,
  label,
  value,
  font,
  boldFont,
  fontSize,
  maxWidth,
  startX,
  startY,
  spacer
) {
  let y = startY

  // Draw the label
  const labelWidth = font.widthOfTextAtSize(label, fontSize)
  page.drawText(label, {
    x: startX,
    y,
    size: fontSize,
    font,
    color: rgb(0, 0, 0),
  })

  // Draw the value with wrapping
  const valueStartX = startX + labelWidth + spacer
  y = drawTextWithWrapping(page, value, boldFont, fontSize, maxWidth, valueStartX, y)

  // Return the final Y position after drawing the text
  return y
}

// Updated helper function to draw text with line breaks
function drawTextWithWrapping(page, text, font, fontSize, maxWidth, startX, startY) {
  if (!text) return startY // Return unchanged Y if text is null/undefined
  const words = text.split(" ")
  let line = ""
  let y = startY

  words.forEach((word) => {
    const testLine = line + word + " "
    const testWidth = font.widthOfTextAtSize(testLine, fontSize)

    if (testWidth > maxWidth && line !== "") {
      page.drawText(line, {
        x: startX,
        y,
        size: fontSize,
        font,
        color: rgb(0, 0, 0),
      })
      line = word + " "
      y -= fontSize * 1.5 // Move to next line
    } else {
      line = testLine
    }
  })

  page.drawText(line, {
    x: startX,
    y,
    size: fontSize,
    font,
    color: rgb(0, 0, 0),
  })

  // Return the final Y position after drawing the text
  return y - fontSize * 1.5 // Adjust for spacing after the last line
}

//function to split text into lines
// Define a function to draw text with line breaks and specific fonts
function drawTextWithLineBreaks(page, textArray, maxWidth, startPosition, fontSize) {
  // An array to store the words for each line of text
  let lines = []
  // An array to store words in the current line being processed
  let currentLine = []
  // A variable to track the total width of the current line
  let currentLineWidth = 0

  // Loop through each text object in the input array
  textArray.forEach((textObj) => {
    // Skip if text is null/undefined
    if (!textObj.text) return
    // Split the text into individual words
    const words = textObj.text.split(" ")

    // Loop through each word
    words.forEach((word) => {
      // Calculate the width of the word with the specified font and size
      const wordWidth = textObj.font.widthOfTextAtSize(word, fontSize)

      // Check if adding the word to the current line would exceed the maxWidth
      if (currentLineWidth + wordWidth <= maxWidth) {
        // If not, add the word to the current line and update the line width
        currentLine.push({ text: word, font: textObj.font })
        currentLineWidth += wordWidth + textObj.font.widthOfTextAtSize(" ", fontSize)
      } else {
        // If it would, add the current line to the lines array
        lines.push(currentLine)
        // Start a new line with the current word and update the line width
        currentLine = [{ text: word, font: textObj.font }]
        currentLineWidth = wordWidth + textObj.font.widthOfTextAtSize(" ", fontSize)
      }
    })
  })

  // Add the last line to the lines array
  lines.push(currentLine)

  // Start drawing the text at the specified y-coordinate
  let yCoordinate = startPosition.y
  // Loop through each line of words
  lines.forEach((line) => {
    // Start drawing each line at the specified x-coordinate
    let xCoordinate = startPosition.x
    // Loop through each word object in the line
    line.forEach((wordObj) => {
      // Draw the word at the current coordinates, with the specified font and size
      page.drawText(wordObj.text, {
        x: xCoordinate,
        y: yCoordinate,
        size: fontSize,
        font: wordObj.font,
      })
      // Update the x-coordinate for the next word
      xCoordinate += wordObj.font.widthOfTextAtSize(wordObj.text + " ", fontSize)
    })
    // Move down for the next line
    yCoordinate -= fontSize * 1.5
  })

  // Return the total height of the text block, which can be used for further layout
  const textBlockHeight = lines.length * fontSize * 1.5
  return textBlockHeight
}

const composeDisplayName = (base, type) => {
  if (!base) return type || ""

  // Normalize the type string
  const t = String(type || "")
    .trim()
    .toLowerCase()

  // ✅ If there's no type or it's "none", skip it
  if (!t || t === "none") return base

  // Avoid double-prefixing
  const alreadyPrefixed = base.toLowerCase().startsWith(t + " ")
  return alreadyPrefixed ? base : `${type} ${base}`
}

export const generateInvoice = async (
  data,
  language,
  translation,
  documentType,
  hasProformas = false,
  proformasData = [],
  isCreditInvoice = false,
  creditInvoiceData = {},
  manufacturerBankAccountIndex = 0,
  clientBankAccountIndex = 0
) => {
  const robotoMedium = await fetch(robotoMed).then((res) => res.arrayBuffer())
  const robotoRegular = await fetch(robotoReg).then((res) => res.arrayBuffer())
  const robotoItalic = await fetch(robotoIta).then((res) => res.arrayBuffer())
  const robotoItalicL = await fetch(robotoItalicLight).then((res) => res.arrayBuffer())

  const pdfDoc = await PDFDocument.create()
  pdfDoc.registerFontkit(fontkit)

  const font = await pdfDoc.embedFont(robotoRegular)

  const boldFont = await pdfDoc.embedFont(robotoMedium)
  const italicFont = await pdfDoc.embedFont(robotoItalic)
  const italicLight = await pdfDoc.embedFont(robotoItalicL)

  let page = pdfDoc.addPage()
  const { width, height } = page.getSize()

  const padding = 25 // Adjust the padding as needed

  const fontSize = 10
  const textMargin = padding
  const headlineFontSize = 15
  const dateOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
  }

  let pageNumber = 1 // Initialize page number

  //Labels
  //Headline labels
  //number label based on pdf type
  let numberLabel
  if (documentType === "invoice") {
    //if type is 0, then its invoice headline
    numberLabel = translation.numberLabelInvoice
  } else {
    //if type is 1, then its invoice proforma
    numberLabel = translation.numberLabelProforma
  }

  //transform translation to abbreviation
  let languageAbbreviation
  if (language === "Latvian") {
    languageAbbreviation = "lv"
  } else if (language === "English") {
    languageAbbreviation = "en"
  }

  const creationDateLabel = translation.creationDataLabel

  //Manufacturer labels
  //Company data labels
  const mSenderLabel = translation.mSenderLabel
  const mNameLabel = translation.mNameLabel
  const mRegNumberLabel = translation.mRegNumberLabel
  const mVatNumberLabel = translation.mVatNumberLabel
  const companyAddressLabel = translation.companyAddressLabel
  const deliveryAddressLabel = translation.deliveryAddressLabel

  //Bank details manufacturer labels
  const bankMainLabel = translation.bankMainLabel
  const bankNameLabel = translation.bankNameLabel
  const accountNumberLabel = translation.accountNumberLabel
  const bankCodeLabel = translation.bankCodeLabel

  //Client labels
  //Client data labels
  const cReceiverLabel = translation.cReceiverLabel
  const cNameLabel = translation.cNameLabel
  const cRegNumberLabel = translation.cRegNumberLabel
  const cVatNumberLabel = translation.cVatNumberLabel
  const clientsCompanyAddressLabel = translation.clientsCompanyAddressLabel
  const clientsDeliveryAddressLabel = translation.clientsDeliveryAddressLabel

  //Bank details client labels
  const clientsBankMainLabel = translation.clientsBankMainLabel
  const clientsBankNameLabel = translation.clientsBankNameLabel
  const clientsAccountNumberLabel = translation.clientsAccountNumberLabel
  const clientsBankCodeLabel = translation.clientsBankCodeLabel

  //pdf type
  let headlineText
  if (documentType === "invoice") {
    //if type is 0, then its invoice headline
    headlineText = translation.pdfInvoice
  } else {
    //if type is 1, then its invoice proforma
    headlineText = translation.pdfProforma
  }

  const headline = headlineText

  // Convert the number to a string and add leading zeros
  const formattedProformaNumber = String(data.docNumber).padStart(4, "0")
  const proformaNumber = data.docPrefix + formattedProformaNumber

  //date
  let formattedDate
  if (translation.language === "Latvian") {
    formattedDate = data.pdfDate.toLocaleDateString("lv-LV", dateOptions)
  } else {
    formattedDate = data.pdfDate.toLocaleDateString("en-EN", dateOptions)
  }
  const date = formattedDate

  //Price Headers
  // const priceCodeHeaderLabel = translation.priceCodeHeaderLabel;
  const priceNameHeaderLabel = translation.priceNameHeaderLabel
  const priceUnitLabel = translation.priceUnitLabel
  const priceCountLabel = translation.priceCountLabel
  const priceLabel = translation.priceLabel
  const priceSumLabel = translation.priceSumLabel

  //totals labels
  const totalSumLabel = translation.totalSumLabel
  const vatTotalLabel = translation.vatTotalLabel
  const totalAmountLabel = translation.totalAmountLabel
  // const discountLabel = translation.discountLabel
  // const discountAmount = data.totalData.totalProfitDiscount

  //advance pay labels
  const advancePaymentSumLabel = translation.advancePaymentSumLabel
  const advancePaymentVatLabel = translation.advancePaymentVatLabel
  const leftPaymentSum = translation.leftPaymentSum

  //data
  //Manufacturers company data
  const companyName = data.manufacturer.company
  const vatNumber = data.manufacturer.vat
  const regNumber = data.manufacturer.reg
  const companyAddressValue =
    data.manufacturer.legalAddress +
    ", " +
    data.manufacturer.legalCity +
    ", " +
    data.manufacturer.legalPC

  const deliveryAddressValue = data.manufacturerDeliveryAddress

  // Get manufacturer bank details - support both new (array) and old (flat) structure
  let bankNameValue, accountNumberValue, bankCodeValue
  if (data.manufacturer.bankAccounts && Array.isArray(data.manufacturer.bankAccounts)) {
    const selectedAccount = data.manufacturer.bankAccounts[manufacturerBankAccountIndex] || data.manufacturer.bankAccounts[0]
    bankNameValue = selectedAccount?.bankName || ""
    accountNumberValue = selectedAccount?.accountNumber || ""
    bankCodeValue = selectedAccount?.bankCode || ""
  } else {
    // Fall back to old structure
    bankNameValue = data.manufacturer.bankName || ""
    accountNumberValue = data.manufacturer.accountNumber || ""
    bankCodeValue = data.manufacturer.bankCode || ""
  }

  //Clients company data
  const clientsCompanyName = data.client.client
  const clientsVatNumber = data.client.vat
  const clientsRegNumber = data.client.reg
  let clientsCompanyAddressValue

  // If a custom address was passed from the edit form, use it.
  if (data.clientsCompanyAddress) {
    clientsCompanyAddressValue = data.clientsCompanyAddress
  } else {
    // Otherwise, fall back to generating it from the client object fields.
    clientsCompanyAddressValue =
      data.client.street && data.client.city && data.client.postalCode
        ? `${data.client.street}, ${data.client.city}, ${data.client.postalCode}`
        : ""
  }

  const clientsDeliveryAddressValue = data.clientDeliveryAddress

  // Get client bank details - support both new (array) and old (flat) structure
  let clientsBankNameValue, clientsAccountNumberValue, clientsBankCodeValue
  if (data.client.bankAccounts && Array.isArray(data.client.bankAccounts)) {
    const selectedAccount = data.client.bankAccounts[clientBankAccountIndex] || data.client.bankAccounts[0]
    clientsBankNameValue = selectedAccount?.bankName || ""
    clientsAccountNumberValue = selectedAccount?.accountNumber || ""
    clientsBankCodeValue = selectedAccount?.bankCode || ""
  } else {
    // Fall back to old structure
    clientsBankNameValue = data.client.bankName || ""
    clientsAccountNumberValue = data.client.accountNumber || ""
    clientsBankCodeValue = data.client.bankCode || ""
  }

  //signature area
  //issuer
  const issuerNameAndSurname = translation.issuerNameAndSurname
  const signature = translation.signature
  const issueDate = translation.issueDate
  const issueYear = translation.issueYear
  const issueYearLabel = translation.issueYearLabel
  //recipient
  const rSignature = translation.rSignature
  const recipientNameAndSurname = translation.recipientNameAndSurname
  const recipientDate = translation.recipientDate
  const recipientYear = translation.recipientYear
  const recipientYearLabel = translation.recipientYearLabel

  //pay until
  const payUntilLabel = translation.payUntilLabel
  const payUntilDate = data.payUntilFormatted.toString()

  //comment
  const commentLabel = translation.commentLabel
  const commentText = data.commentText.toString()

  //sum with words
  const sumWithWordsLabel = translation.sumWithWordsLabel

  // // Define margins and starting Y-position
  // const pageHeight = page.getHeight()

  // // Calculate the Y-coordinate 50 pixels from the bottom
  // //50 pixels from the bottom
  // const bottomSafeMargin = 60

  const headlineWidth = boldFont.widthOfTextAtSize(headline, headlineFontSize)

  //space between label and value
  const spacer = 5

  //width of creation date
  const creationDateLabelWidth = font.widthOfTextAtSize(creationDateLabel, fontSize)
  //width of invoice number label
  const numberLabelWidth = font.widthOfTextAtSize(numberLabel, fontSize)

  const headlineX = (width - headlineWidth) / 2
  const headlineY = height - 60 // Adjust the vertical position as needed
  const textX = textMargin
  const textY = headlineY - 30
  const dateX = creationDateLabelWidth + textMargin + spacer
  const dateY = textY
  const proformaNumberX = numberLabelWidth + textMargin + spacer
  const proformaLabelX = textX
  const proformaNumberY = textY - 15

  //logo only if art label is selected
  // Fetch the logo image from the provided URL
  let logoImage
  const logoImageBytes = await fetch(data.manufacturer.logoUrl).then((res) => res.arrayBuffer())

  try {
    // First try embedding as JPEG
    logoImage = await pdfDoc.embedJpg(logoImageBytes)
  } catch (error) {
    try {
      // If that fails, try embedding as PNG
      logoImage = await pdfDoc.embedPng(logoImageBytes)
    } catch (error) {
      console.error("Failed to embed the logo image as JPEG or PNG:", error)
      return
    }
  }

  // Draw the logo in the top-right corner of the page
  const logoX = width - logoImage.width / 5 + 25
  const logoY = height - logoImage.height / 5 + 60
  page.drawImage(logoImage, {
    x: logoX,
    y: logoY,
    width: logoImage.width / 6,
    height: logoImage.height / 6,
  })

  drawPageNumber(
    page,
    pageNumber,
    fontSize,
    font,
    translation.paymentDocumentText,
    proformaNumber,
    boldFont,
    italicFont
  )

  page.drawText(headline, {
    x: headlineX,
    y: headlineY,
    size: headlineFontSize,
    font: boldFont,
    color: rgb(0, 0, 0),
  })
  page.drawText(creationDateLabel, {
    x: textX,
    y: textY,
    size: fontSize,
    font,
    color: rgb(0, 0, 0),
  })
  page.drawText(date, { x: dateX, y: dateY, size: fontSize, font: boldFont, color: rgb(0, 0, 0) })
  page.drawText(numberLabel, {
    x: proformaLabelX,
    y: proformaNumberY,
    size: fontSize,
    font,
    color: rgb(0, 0, 0),
  })
  page.drawText(proformaNumber, {
    x: proformaNumberX,
    y: proformaNumberY,
    size: fontSize,
    font: boldFont,
    color: rgb(0, 0, 0),
  })

  // Add a vertical line under the last text
  const lineStartX = padding
  const lineStartY = proformaNumberY - 25
  const lineEndX = width - padding
  const lineEndY = lineStartY
  page.drawLine({
    start: { x: lineStartX, y: lineStartY },
    end: { x: lineEndX, y: lineEndY },
    thickness: 1,
    color: rgb(0, 0, 0),
  })

  // Add the additional lines of text
  const labelTextX = textX
  const labelTextY = lineStartY
  const valueTextY = labelTextY
  const labelValueSpacing = 15

  //add sender text
  page.drawText(mSenderLabel, {
    x: labelTextX,
    y: lineStartY + 5,
    size: 9,
    font: italicFont,
    color: rgb(0, 0, 0),
  })

  //SENDER DATA
  let currentSenderY = labelTextY - 15 // Starting Y position for the sender text block
  const senderStartX = labelTextX // Starting X position for the sender text block

  // Drawing sender fields with the dynamic function
  currentSenderY = drawTextWithLabel(
    page,
    mNameLabel,
    companyName,
    font,
    boldFont,
    fontSize,
    maxTextWidth,
    senderStartX,
    currentSenderY,
    spacer
  )
  currentSenderY = drawTextWithLabel(
    page,
    mRegNumberLabel,
    regNumber,
    font,
    boldFont,
    fontSize,
    maxTextWidth,
    senderStartX,
    currentSenderY,
    spacer
  )
  currentSenderY = drawTextWithLabel(
    page,
    mVatNumberLabel,
    vatNumber,
    font,
    boldFont,
    fontSize,
    maxTextWidth,
    senderStartX,
    currentSenderY,
    spacer
  )
  currentSenderY = drawTextWithLabel(
    page,
    companyAddressLabel,
    companyAddressValue,
    font,
    boldFont,
    fontSize,
    maxTextWidth,
    senderStartX,
    currentSenderY,
    spacer
  )
  currentSenderY = drawTextWithLabel(
    page,
    deliveryAddressLabel,
    deliveryAddressValue,
    font,
    boldFont,
    fontSize,
    maxTextWidth,
    senderStartX,
    currentSenderY,
    spacer
  )

  // Add a vertical line between the first and second columns
  const verticalLineX = textX + 340
  const verticalLineStartY = labelTextY - labelValueSpacing * 4.5
  const verticalLineEndY = labelTextY - labelValueSpacing * 0.5
  page.drawLine({
    start: { x: verticalLineX, y: verticalLineStartY },
    end: { x: verticalLineX, y: verticalLineEndY },
    thickness: 1,
    color: rgb(0, 0, 0),
    opacity: 0.5,
  })

  //label widths
  const bankNameLabelWidth = font.widthOfTextAtSize(bankNameLabel, fontSize)
  const accountNumberLabelWidth = font.widthOfTextAtSize(accountNumberLabel, fontSize)
  const bankCodeLabelWidth = font.widthOfTextAtSize(bankCodeLabel, fontSize)

  // Add the second column
  const label2TextX = labelTextX + 350

  page.drawText(bankMainLabel, {
    x: label2TextX,
    y: labelTextY - labelValueSpacing,
    size: fontSize,
    font,
    color: rgb(0, 0, 0),
  })

  page.drawText(bankNameLabel, {
    x: label2TextX,
    y: labelTextY - labelValueSpacing * 2,
    size: fontSize,
    font,
    color: rgb(0, 0, 0),
  })
  page.drawText(bankNameValue, {
    x: label2TextX + bankNameLabelWidth + spacer,
    y: valueTextY - labelValueSpacing * 2,
    size: fontSize,
    font: boldFont,
    color: rgb(0, 0, 0),
  })

  page.drawText(accountNumberLabel, {
    x: label2TextX,
    y: labelTextY - labelValueSpacing * 3,
    size: fontSize,
    font,
    color: rgb(0, 0, 0),
  })
  page.drawText(accountNumberValue, {
    x: label2TextX + accountNumberLabelWidth + spacer,
    y: valueTextY - labelValueSpacing * 3,
    size: fontSize,
    font: boldFont,
    color: rgb(0, 0, 0),
  })

  page.drawText(bankCodeLabel, {
    x: label2TextX,
    y: labelTextY - labelValueSpacing * 4,
    size: fontSize,
    font,
    color: rgb(0, 0, 0),
  })
  page.drawText(bankCodeValue, {
    x: label2TextX + bankCodeLabelWidth + spacer,
    y: valueTextY - labelValueSpacing * 4,
    size: fontSize,
    font: boldFont,
    color: rgb(0, 0, 0),
  })

  // Add a horizontal line under the bankCodeValue
  const lineStartX2 = padding
  const lineStartY2 = currentSenderY - 15
  const lineEndX2 = width - padding
  const lineEndY2 = lineStartY2

  page.drawLine({
    start: { x: lineStartX2, y: lineStartY2 },
    end: { x: lineEndX2, y: lineEndY2 },
    thickness: 1,
    color: rgb(0, 0, 0),
  })

  //add receiver text
  page.drawText(cReceiverLabel, {
    x: labelTextX,
    y: lineStartY2 + 5,
    size: 9,
    font: italicFont,
    color: rgb(0, 0, 0),
  })

  //clients info
  // Add the additional lines of text
  //2
  const label3TextX = lineStartX2
  const label3TextY = lineStartY2
  const value3TextY = label3TextY

  // Example usage for drawing multiple fields dynamically
  let currentY = lineStartY2 - 15 // Starting Y position for the text block
  const startX = label3TextX // Starting X position for the text block

  // Drawing multiple fields with the dynamic function
  currentY = drawTextWithLabel(
    page,
    cNameLabel,
    clientsCompanyName,
    font,
    boldFont,
    fontSize,
    maxTextWidth,
    startX,
    currentY,
    spacer
  )
  currentY = drawTextWithLabel(
    page,
    cRegNumberLabel,
    clientsRegNumber,
    font,
    boldFont,
    fontSize,
    maxTextWidth,
    startX,
    currentY,
    spacer
  )
  currentY = drawTextWithLabel(
    page,
    cVatNumberLabel,
    clientsVatNumber,
    font,
    boldFont,
    fontSize,
    maxTextWidth,
    startX,
    currentY,
    spacer
  )
  currentY = drawTextWithLabel(
    page,
    clientsCompanyAddressLabel,
    clientsCompanyAddressValue,
    font,
    boldFont,
    fontSize,
    maxTextWidth,
    startX,
    currentY,
    spacer
  )
  currentY = drawTextWithLabel(
    page,
    clientsDeliveryAddressLabel,
    clientsDeliveryAddressValue,
    font,
    boldFont,
    fontSize,
    maxTextWidth,
    startX,
    currentY,
    spacer
  )

  // Add the second column

  //label widths for second row banking
  const clientsBankNameLabelWidth = font.widthOfTextAtSize(clientsBankNameLabel, fontSize)
  const clientsAccountNumberLabelWidth = font.widthOfTextAtSize(clientsAccountNumberLabel, fontSize)
  const clientsBankCodeLabelWidth = font.widthOfTextAtSize(clientsBankCodeLabel, fontSize)

  //2
  const label4TextX = label3TextX + 350

  page.drawText(clientsBankMainLabel, {
    x: label4TextX,
    y: label3TextY - labelValueSpacing,
    size: fontSize,
    font,
    color: rgb(0, 0, 0),
  })

  page.drawText(clientsBankNameLabel, {
    x: label4TextX,
    y: label3TextY - labelValueSpacing * 2,
    size: fontSize,
    font,
    color: rgb(0, 0, 0),
  })
  page.drawText(clientsBankNameValue, {
    x: label4TextX + clientsBankNameLabelWidth + spacer,
    y: value3TextY - labelValueSpacing * 2,
    size: fontSize,
    font: boldFont,
    color: rgb(0, 0, 0),
  })

  page.drawText(clientsAccountNumberLabel, {
    x: label4TextX,
    y: label3TextY - labelValueSpacing * 3,
    size: fontSize,
    font,
    color: rgb(0, 0, 0),
  })
  page.drawText(clientsAccountNumberValue, {
    x: label4TextX + clientsAccountNumberLabelWidth + spacer,
    y: value3TextY - labelValueSpacing * 3,
    size: fontSize,
    font: boldFont,
    color: rgb(0, 0, 0),
  })

  page.drawText(clientsBankCodeLabel, {
    x: label4TextX,
    y: label3TextY - labelValueSpacing * 4,
    size: fontSize,
    font,
    color: rgb(0, 0, 0),
  })
  page.drawText(clientsBankCodeValue, {
    x: label4TextX + clientsBankCodeLabelWidth + spacer,
    y: value3TextY - labelValueSpacing * 4,
    size: fontSize,
    font: boldFont,
    color: rgb(0, 0, 0),
  })

  // Add a vertical line between the first and second columns
  //2
  const vertical2LineX = lineStartX2 + 340
  const vertical2LineStartY = label3TextY - labelValueSpacing * 4.5
  const vertical2LineEndY = label3TextY - labelValueSpacing * 0.5
  page.drawLine({
    start: { x: vertical2LineX, y: vertical2LineStartY },
    end: { x: vertical2LineX, y: vertical2LineEndY },
    thickness: 1,
    color: rgb(0, 0, 0),
    opacity: 0.5,
  })

  //4
  // Add a horizontal line under the previous horizontal like
  const lineStartX4 = padding
  const lineStartY4 = currentY
  const lineEndX4 = width - padding
  const lineEndY4 = lineStartY4
  page.drawLine({
    start: { x: lineStartX4, y: lineStartY4 },
    end: { x: lineEndX4, y: lineEndY4 },
    thickness: 1,
    color: rgb(0, 0, 0),
  })

  //5
  //adds vertical line start
  const vertical3LineX = lineStartX4
  const vertical3LineStartY = lineStartY4
  const vertical3LineEndY = vertical3LineStartY - labelValueSpacing
  page.drawLine({
    start: { x: vertical3LineX, y: vertical3LineStartY },
    end: { x: vertical3LineX, y: vertical3LineEndY },
    thickness: 1,
    color: rgb(0, 0, 0),
  })

  //adds vertical line end
  const vertical4LineX = lineEndX4
  const vertical4LineStartY = lineStartY4
  const vertical4LineEndY = vertical4LineStartY - labelValueSpacing
  page.drawLine({
    start: { x: vertical4LineX, y: vertical4LineStartY },
    end: { x: vertical4LineX, y: vertical4LineEndY },
    thickness: 1,
    color: rgb(0, 0, 0),
  })

  const tableHeaderX = vertical3LineX + 15
  const tableHeaderY = lineStartY4 + 4

  //adds first text
  // page.drawText(priceCodeHeaderLabel, { x: tableHeaderX, y: tableHeaderY - labelValueSpacing, size: fontSize, font, color: rgb(0, 0, 0) });

  //adds second text
  page.drawText(priceNameHeaderLabel, {
    x: vertical3LineX + 5,
    y: tableHeaderY - labelValueSpacing,
    size: fontSize,
    font,
    color: rgb(0, 0, 0),
  })
  //adds third text
  page.drawText(priceUnitLabel, {
    x: tableHeaderX * 10,
    y: tableHeaderY - labelValueSpacing,
    size: fontSize,
    font,
    color: rgb(0, 0, 0),
  })
  //adds fourth text
  page.drawText(priceCountLabel, {
    x: tableHeaderX * 11,
    y: tableHeaderY - labelValueSpacing,
    size: fontSize,
    font,
    color: rgb(0, 0, 0),
  })
  //adds fourth text
  page.drawText(priceLabel, {
    x: tableHeaderX * 12,
    y: tableHeaderY - labelValueSpacing,
    size: fontSize,
    font,
    color: rgb(0, 0, 0),
  })
  //adds fourth text
  page.drawText(priceSumLabel, {
    x: tableHeaderX * 13,
    y: tableHeaderY - labelValueSpacing,
    size: fontSize,
    font,
    color: rgb(0, 0, 0),
  })

  //6
  // Add a horizontal line under the previous horizontal like
  const lineStartX5 = padding
  const lineStartY5 = vertical3LineEndY
  const lineEndX5 = width - padding
  const lineEndY5 = vertical4LineEndY
  page.drawLine({
    start: { x: lineStartX5, y: lineStartY5 },
    end: { x: lineEndX5, y: lineEndY5 },
    thickness: 1,
    color: rgb(0, 0, 0),
  })

  const productValueY = tableHeaderY - 35

  //sets sums array
  const sums = []
  //sets last maps axis
  let lastMapY = 0

  // Initialize a variable to store the previous item's "hasProductComment" value
  let previousHeight = 0

  const minMargin = 60 // Minimum margin from the bottom
  let remainingSpace = lineStartY5 // start of the content
  // //init cursor position
  let cursorY = productValueY

  // Create a testPrices array with 30 times more items.
  // const testPrices = []
  // for (let i = 0; i < 30; i++) {
  //   testPrices.push(...data.prices)
  // }
  // data.prices = testPrices

  //loops over the objects
  data.prices.forEach((item, index) => {
    const { product, count, price, productComment, hasProductComment, unit } = item

    // ✅ SKIP RENDERING DISCOUNT/SURCHARGE IN THE PRODUCT TABLE
    if (product === "Discount" || product === "Surcharge") {
      return null
    }

    //unit string
    let unitValue

    //translated unit string
    let unitString
    if (unit === "pcs.") {
      unitString = translation.pcsString
      unitValue = Number(1)
    } else {
      unitString = translation.priceUnit
      unitValue = Number(1000)
    }

    let sum = (count * price) / unitValue
    sums.push(sum)

    //updates count since its string
    let updatedCount = count / unitValue

    //translation for the new plate
    let productOrPlate
    if (product === "New Plate") {
      productOrPlate = translation.newPlateName
    } else {
      productOrPlate = composeDisplayName(product, item.productType)
    }

    const formattedNumber = sum.toFixed(2)
    const pcsString = unitString
    const countString = updatedCount.toString() //also divides by thousand
    const priceString = Number(price).toFixed(2)
    const sumString = formattedNumber.toString()

    const productCommentWithCheck = hasProductComment ? `(${productComment})` : ""

    // Combine product name and comment text, each with their own font/style
    const textToDraw = [
      { text: productOrPlate, font: boldFont },
      { text: productCommentWithCheck, font: italicLight },
    ]

    //height of this block
    const thisBlockHeight = calculateTextBlockHeight(textToDraw, tableHeaderX * 9, fontSize)

    if (remainingSpace - thisBlockHeight < minMargin) {
      // Add a new page and reset the remaining space
      page = pdfDoc.addPage([595.28, 841.89])

      pageNumber++
      drawPageNumber(
        page,
        pageNumber,
        fontSize,
        font,
        translation.paymentDocumentText,
        proformaNumber,
        boldFont,
        italicFont
      )
      remainingSpace = 841.89 // Resetting the remaining space to full for new page
      previousHeight = 45 // Resetting previous height as we are on a new page
      cursorY = 841.89

      // Define your labels
      const labels = {
        priceNameHeaderLabel: priceNameHeaderLabel,
        priceUnitLabel: priceUnitLabel,
        priceCountLabel: priceCountLabel,
        priceLabel: priceLabel,
        priceSumLabel: priceSumLabel,
      }

      // Use the function to draw the header
      drawNewProductTableHeader(
        page, // Your page object
        cursorY, // Your current cursorY position
        20, // Example labelValueSpacing value
        fontSize, // Your defined fontSize
        font, // Your defined font
        tableHeaderX, // Your defined tableHeaderX
        vertical3LineX, // Your defined vertical3LineX
        labels // The defined labels
      )

      // Add a horizontal line under the previous horizontal like
      page.drawLine({
        start: { x: lineStartX4, y: cursorY - labelValueSpacing - 10 },
        end: { x: lineEndX4, y: cursorY - labelValueSpacing - 10 },
        thickness: 1,
        color: rgb(0, 0, 0),
      })

      // Add a horizontal line under the previous horizontal like
      page.drawLine({
        start: { x: lineStartX4, y: cursorY - 10 },
        end: { x: lineEndX4, y: cursorY - 10 },
        thickness: 1,
        color: rgb(0, 0, 0),
      })

      //5
      page.drawLine({
        start: { x: vertical3LineX, y: cursorY - labelValueSpacing - 10 },
        end: { x: vertical3LineX, y: cursorY - 10 },
        thickness: 1,
        color: rgb(0, 0, 0),
      })

      //adds vertical line end
      page.drawLine({
        start: { x: vertical4LineX, y: cursorY - labelValueSpacing - 10 },
        end: { x: vertical4LineX, y: cursorY - 10 },
        thickness: 1,
        color: rgb(0, 0, 0),
      })
    }

    const totalHeightOfProductText = drawTextWithLineBreaks(
      page,
      textToDraw,
      tableHeaderX * 9,
      { x: vertical3LineX + 5, y: cursorY - previousHeight },
      10
    )

    let currentLine = totalHeightOfProductText

    page.drawText(pcsString, {
      x: tableHeaderX * 10,
      y: cursorY - previousHeight,
      size: fontSize,
      font,
      color: rgb(0, 0, 0),
    })
    page.drawText(countString, {
      x: tableHeaderX * 11,
      y: cursorY - previousHeight,
      size: fontSize,
      font,
      color: rgb(0, 0, 0),
    })
    page.drawText(priceString, {
      x: tableHeaderX * 12,
      y: cursorY - previousHeight,
      size: fontSize,
      font,
      color: rgb(0, 0, 0),
    })
    page.drawText(sumString, {
      x: tableHeaderX * 13,
      y: cursorY - previousHeight,
      size: fontSize,
      font,
      color: rgb(0, 0, 0),
    })

    //updates the last map
    if (hasProductComment) {
      //based on last products comment the lastMapY
      // Update lastMapY and lastMapX based on the current iteration
      lastMapY = cursorY - previousHeight - totalHeightOfProductText
    } else {
      lastMapY = cursorY - previousHeight //without the last comment
    }

    // Update the previousHasProductComment variable
    //adds the current line to the total height
    previousHeight = previousHeight + currentLine

    // Update the remaining space
    remainingSpace -= thisBlockHeight

    return null
  })

  //horizontal line over the sum
  const lineStartX6 = tableHeaderX * 8
  const lineStartY6 = lastMapY - 30
  const lineEndX6 = width - padding
  const lineEndY6 = lineStartY6
  page.drawLine({
    start: { x: lineStartX6 + 15, y: lineStartY6 },
    end: { x: lineEndX6 - 5, y: lineEndY6 },
    thickness: 1,
    color: rgb(0, 0, 0),
  })
  //gets the total sum
  const totalSum = sums
    .reduce((accumulator, currentValue) => accumulator + currentValue, 0)
    .toFixed(2)

  // Align text to the right by subtracting the text width from the desired alignment column (right edge)
  const rightAlignX = tableHeaderX * 14 // adjust multiplier if needed to shift further right

  // ✅ DEFINE THESE VARIABLES FIRST - BEFORE THE IF/ELSE BLOCK
  const vatAmount = data.totalData.totalVat.toFixed(2)
  const totalAmountWithVat = data.totalData.totalSum.toFixed(2)

  const hasDiscount = data.totalData.totalProfitDiscount !== 0
  const discountAmount = data.totalData.totalProfitDiscount

  // Calculate subtotal (before discount)
  const subtotalBeforeDiscount = totalSum

  // ✅ IF there's a discount, show subtotal first, then discount, then neto sum
  if (hasDiscount) {
    // Show Subtotal (before discount)
    page.drawText(translation.subtotalLabel || "Subtotal:", {
      x: tableHeaderX * 8.5,
      y: lineStartY6 - 15,
      size: fontSize,
      font,
      color: rgb(0, 0, 0),
    })
    const subtotalText = subtotalBeforeDiscount.toString()
    const subtotalWidth = boldFont.widthOfTextAtSize(subtotalText, fontSize)
    page.drawText(subtotalText, {
      x: rightAlignX - subtotalWidth,
      y: lineStartY6 - 15,
      size: fontSize,
      font: boldFont,
      color: rgb(0, 0, 0),
    })

    // Show Discount/Surcharge
    const discountLabelText =
      discountAmount > 0
        ? (translation.surchargeName || "Surcharge") + ":"
        : (translation.discountName || "Discount") + ":"

    page.drawText(discountLabelText, {
      x: tableHeaderX * 8.5,
      y: lineStartY6 - 30,
      size: fontSize,
      font,
      color: rgb(0, 0, 0),
    })
    const discountText = discountAmount.toFixed(2)
    const discountWidth = boldFont.widthOfTextAtSize(discountText, fontSize)
    page.drawText(discountText, {
      x: rightAlignX - discountWidth,
      y: lineStartY6 - 30,
      size: fontSize,
      font: boldFont,
      color: rgb(0, 0, 0), // ✅ BLACK instead of red/green
    })

    // ✅ ADD HORIZONTAL LINE AFTER DISCOUNT
    page.drawLine({
      start: { x: tableHeaderX * 8.5, y: lineStartY6 - 37 },
      end: { x: lineEndX6 - 5, y: lineStartY6 - 37 },
      thickness: 0.5,
      color: rgb(0, 0, 0),
    })

    // Show Neto Sum (after discount) - now with more spacing
    page.drawText(totalSumLabel, {
      x: tableHeaderX * 8.5,
      y: lineStartY6 - 50, // ✅ Adjusted from -45 to -50 for spacing after line
      size: fontSize,
      font,
      color: rgb(0, 0, 0),
    })
    const netoAfterDiscount = (parseFloat(totalSum) + parseFloat(discountAmount)).toFixed(2)
    const totalSumText = netoAfterDiscount.toString()
    const totalSumWidth = boldFont.widthOfTextAtSize(totalSumText, fontSize)
    page.drawText(totalSumText, {
      x: rightAlignX - totalSumWidth,
      y: lineStartY6 - 50, // ✅ Adjusted from -45 to -50
      size: fontSize,
      font: boldFont,
      color: rgb(0, 0, 0),
    })

    // Adjust Y position for VAT and Total
    const vatYPos = lineStartY6 - 65 // ✅ Adjusted from -60 to -65
    const totalYPos = lineStartY6 - 80 // ✅ Adjusted from -75 to -80

    // VAT
    page.drawText(vatTotalLabel, {
      x: tableHeaderX * 8.5,
      y: vatYPos,
      size: fontSize,
      font,
      color: rgb(0, 0, 0),
    })
    const vatAmountText = vatAmount.toString()
    const vatAmountWidth = boldFont.widthOfTextAtSize(vatAmountText, fontSize)
    page.drawText(vatAmountText, {
      x: rightAlignX - vatAmountWidth,
      y: vatYPos,
      size: fontSize,
      font: boldFont,
      color: rgb(0, 0, 0),
    })

    // Total with VAT
    page.drawText(totalAmountLabel, {
      x: tableHeaderX * 8.5,
      y: totalYPos,
      size: fontSize,
      font,
      color: rgb(0, 0, 0),
    })
    const totalWithVatText = totalAmountWithVat.toString()
    const totalWithVatWidth = boldFont.widthOfTextAtSize(totalWithVatText, fontSize)
    page.drawText(totalWithVatText, {
      x: rightAlignX - totalWithVatWidth,
      y: totalYPos,
      size: fontSize,
      font: boldFont,
      color: rgb(0, 0, 0),
    })

    // Update lastMapY for subsequent sections
    lastMapY = totalYPos + 45
  } else {
    // ✅ NO DISCOUNT: Keep existing layout
    // Total sum
    const totalSumText = totalSum.toString()
    const totalSumWidth = boldFont.widthOfTextAtSize(totalSumText, fontSize)
    page.drawText(totalSumText, {
      x: rightAlignX - totalSumWidth,
      y: lineStartY6 - 15,
      size: fontSize,
      font: boldFont,
      color: rgb(0, 0, 0),
    })
    //total sum label
    page.drawText(totalSumLabel, {
      x: tableHeaderX * 8.5,
      y: lineStartY6 - 15,
      size: fontSize,
      font,
      color: rgb(0, 0, 0),
    })

    // Display the VAT and total amount with VAT
    page.drawText(vatTotalLabel, {
      x: tableHeaderX * 8.5,
      y: lineStartY6 - 30,
      size: fontSize,
      font,
      color: rgb(0, 0, 0),
    })
    const vatAmountText = vatAmount.toString()
    const vatAmountWidth = boldFont.widthOfTextAtSize(vatAmountText, fontSize)
    page.drawText(vatAmountText, {
      x: rightAlignX - vatAmountWidth,
      y: lineStartY6 - 30,
      size: fontSize,
      font: boldFont,
      color: rgb(0, 0, 0),
    })

    page.drawText(totalAmountLabel, {
      x: tableHeaderX * 8.5,
      y: lineStartY6 - 45,
      size: fontSize,
      font,
      color: rgb(0, 0, 0),
    })
    const totalWithVatText = totalAmountWithVat.toString()
    const totalWithVatWidth = boldFont.widthOfTextAtSize(totalWithVatText, fontSize)
    page.drawText(totalWithVatText, {
      x: rightAlignX - totalWithVatWidth,
      y: lineStartY6 - 45,
      size: fontSize,
      font: boldFont,
      color: rgb(0, 0, 0),
    })
  }

  // Proforma section
  if (hasProformas) {
    let proformaRefLabelText = ""
    if (proformasData.proformasCodes.length === 1) {
      proformaRefLabelText = translation.proformaRefLabel + " " + proformasData.proformasCodes[0]
    } else {
      const codesDisplay = proformasData.proformasCodes.join(", ")
      proformaRefLabelText = translation.proformaRefLabelMany + " " + codesDisplay
    }

    // Set the available width to 50 pixels by default.
    const availableWidth = page.getWidth() - tableHeaderX * 8.5 - 20

    // Draw the proforma reference text using the wrapping function.
    // Capture the new Y coordinate after drawing.
    const newY = drawTextWithWrapping(
      page,
      proformaRefLabelText,
      italicFont,
      9,
      availableWidth,
      tableHeaderX * 8.5,
      lineStartY6 - 65
    )

    // Draw a horizontal line under the wrapped proforma text.
    page.drawLine({
      start: { x: lineStartX6 + 15, y: newY + 8 },
      end: { x: lineEndX6 - 15, y: newY + 8 },
      thickness: 0.5,
      color: rgb(0, 0, 0),
    })

    // Payment details from proformas
    // const advancedPaymentSum = proformasData.proformasTotalNeto.toString()
    const advancePaymentVat = proformasData.proformasTotalVat.toString()
    const advancePaymentTotal = proformasData.proformasTotalSum.toString()
    const leftPaymentSumValue = (totalAmountWithVat - proformasData.proformasTotalSum).toFixed(2)

    // Draw payment details starting below the proforma text,
    // using newY to avoid any overlap.
    page.drawText(advancePaymentSumLabel, {
      x: tableHeaderX * 8.5,
      y: newY - 5,
      size: fontSize,
      font,
      color: rgb(0, 0, 0),
    })
    page.drawText(advancePaymentTotal, {
      x: tableHeaderX * 13,
      y: newY - 5,
      size: fontSize,
      font: boldFont,
      color: rgb(0, 0, 0),
    })
    page.drawText(advancePaymentVatLabel, {
      x: tableHeaderX * 8.5,
      y: newY - 20,
      size: fontSize,
      font,
      color: rgb(0, 0, 0),
    })
    page.drawText(advancePaymentVat, {
      x: tableHeaderX * 13,
      y: newY - 20,
      size: fontSize,
      font: boldFont,
      color: rgb(0, 0, 0),
    })
    page.drawText(leftPaymentSum, {
      x: tableHeaderX * 8.5,
      y: newY - 35,
      size: fontSize,
      font,
      color: rgb(0, 0, 0),
    })
    page.drawText(leftPaymentSumValue.toString(), {
      x: tableHeaderX * 13,
      y: newY - 35,
      size: fontSize,
      font: boldFont,
      color: rgb(0, 0, 0),
    })

    // Optionally, update lastMapY if needed for subsequent layout:
    lastMapY = newY + 40
  }

  if (isCreditInvoice && creditInvoiceData.creditInvoiceCode) {
    const creditRefLabelText = translation.creditInvoiceRefLabel
      ? `${translation.creditInvoiceRefLabel} ${creditInvoiceData.creditInvoiceCode}`
      : `Credit for invoice: ${creditInvoiceData.creditInvoiceCode}`

    const availableWidth = page.getWidth() - tableHeaderX * 8.5 - 20

    const newY = drawTextWithWrapping(
      page,
      creditRefLabelText,
      italicFont,
      9,
      availableWidth,
      tableHeaderX * 8.5,
      lineStartY6 - 65
    )

    page.drawLine({
      start: { x: lineStartX6 + 15, y: newY + 8 },
      end: { x: lineEndX6 - 15, y: newY + 8 },
      thickness: 0.5,
      color: rgb(0, 0, 0),
    })

    // Display original invoice amounts
    const originalInvoiceLabel =
      translation.originalInvoiceAmountLabel || "Original Invoice Amount:"
    const creditAmountLabel = translation.creditAmountLabel || "Credit Amount:"
    const balanceLabel = translation.balanceAmountLabel || "Balance:"

    const originalAmount = creditInvoiceData.creditInvoiceTotalSum.toString()
    const creditAmount = totalAmountWithVat.toString()
    const balanceValue = (creditInvoiceData.creditInvoiceTotalSum - totalAmountWithVat).toFixed(2)

    page.drawText(originalInvoiceLabel, {
      x: tableHeaderX * 8.5,
      y: newY - 5,
      size: fontSize,
      font,
      color: rgb(0, 0, 0),
    })
    page.drawText(originalAmount, {
      x: tableHeaderX * 13,
      y: newY - 5,
      size: fontSize,
      font: boldFont,
      color: rgb(0, 0, 0),
    })

    page.drawText(creditAmountLabel, {
      x: tableHeaderX * 8.5,
      y: newY - 20,
      size: fontSize,
      font,
      color: rgb(0, 0, 0),
    })
    page.drawText(creditAmount, {
      x: tableHeaderX * 13,
      y: newY - 20,
      size: fontSize,
      font: boldFont,
      color: rgb(0, 0, 0),
    })

    page.drawText(balanceLabel, {
      x: tableHeaderX * 8.5,
      y: newY - 35,
      size: fontSize,
      font,
      color: rgb(0, 0, 0),
    })
    page.drawText(balanceValue.toString(), {
      x: tableHeaderX * 13,
      y: newY - 35,
      size: fontSize,
      font: boldFont,
      color: rgb(0, 0, 0),
    })

    lastMapY = newY + 40
  }

  //label
  //length of the label
  const sumWithWordsLabelWidth = font.widthOfTextAtSize(sumWithWordsLabel, fontSize)
  page.drawText(sumWithWordsLabel, {
    x: tableHeaderX - 15,
    y: lastMapY - 110,
    size: fontSize,
    font,
    color: rgb(0, 0, 0),
  })

  //sum with words
  page.drawText(numberToWords(totalAmountWithVat, languageAbbreviation), {
    x: tableHeaderX + sumWithWordsLabelWidth - 10,
    y: lastMapY - 110,
    size: fontSize,
    font: boldFont,
    color: rgb(0, 0, 0),
  })

  //horizontal line under the sum
  const lineStartX7 = padding
  const lineStartY7 = lastMapY - 115 // just change this parameter to get the bottom down
  const lineEndX7 = width - padding
  const lineEndY7 = lineStartY7

  //adds vertical line under horizontal line
  const vertical5LineX = width / 2
  const vertical5LineStartY = lineStartY7
  const vertical5LineEndY = vertical5LineStartY - labelValueSpacing * 3.5

  //issuer
  const textYLineStart = lineStartY7

  //   //text widths
  const iNameWidth = lineStartX7 + font.widthOfTextAtSize(issuerNameAndSurname, fontSize) + 5
  const iSignatureWidth = lineStartX7 + font.widthOfTextAtSize(signature, fontSize) + 5
  const iDateWidth = lineStartX7 + font.widthOfTextAtSize(issueDate, fontSize) + 5
  const iYearWidth = iDateWidth + font.widthOfTextAtSize(issueYear, fontSize)
  const iYearUnderlineEnd = iYearWidth + 15
  const iYearLabelEnd = iYearUnderlineEnd + font.widthOfTextAtSize(issueYearLabel, fontSize)
  const iMonthDateEnd = iYearLabelEnd + 20

  //underline
  const underlineEndX = width / 2 - 15

  //comment section
  //comment label array with comment part
  const commentTextToDraw = [
    { text: commentLabel, font: boldFont },
    { text: `${commentText}`, font: italicLight },
  ]

  let requiredCommentHeight = calculateTextBlockHeight(commentTextToDraw, lineEndX7 - 65, fontSize)

  //creates a y position which can be changed if the content does not fit
  let bottomSectionYPos = textYLineStart
  let bottomSectionYLineStart = lineStartY7
  let bottomSectionYLineEnd = lineEndY7
  let bottomSectionVerticalLineYStart = vertical5LineStartY
  let bottomSectionVerticalLineYEnd = vertical5LineEndY
  let bottomSectionVerticalLineX = vertical5LineX

  //checks if content needs a new page
  if (75 + requiredCommentHeight > remainingSpace - minMargin) {
    page = pdfDoc.addPage([595.28, 841.89])
    pageNumber++
    drawPageNumber(
      page,
      pageNumber,
      fontSize,
      font,
      translation.paymentDocumentText,
      proformaNumber,
      boldFont,
      italicFont
    )
    bottomSectionYPos = 800
    bottomSectionYLineStart = bottomSectionYPos + 5
    bottomSectionYLineEnd = bottomSectionYPos + 5
    bottomSectionVerticalLineYStart = bottomSectionYPos + 5
    bottomSectionVerticalLineYEnd = bottomSectionYPos - labelValueSpacing * 3.5
  }

  //horizontal line for name and surname
  page.drawLine({
    start: { x: lineStartX7, y: bottomSectionYLineStart },
    end: { x: lineEndX7, y: bottomSectionYLineEnd },
    thickness: 1,
    opacity: 0.5,
    color: rgb(0, 0, 0),
  })

  //vertical line for name and surname
  page.drawLine({
    start: { x: bottomSectionVerticalLineX, y: bottomSectionVerticalLineYStart },
    end: { x: bottomSectionVerticalLineX, y: bottomSectionVerticalLineYEnd },
    thickness: 1,
    color: rgb(0, 0, 0),
    opacity: 0.5,
  })

  //name and surname
  page.drawText(issuerNameAndSurname, {
    x: lineStartX7,
    y: bottomSectionYPos - labelValueSpacing,
    size: fontSize,
    font,
    color: rgb(0, 0, 0),
  })
  page.drawLine({
    start: { x: iNameWidth, y: bottomSectionYPos - labelValueSpacing },
    end: { x: underlineEndX, y: bottomSectionYPos - labelValueSpacing },
    thickness: 1,
    color: rgb(0, 0, 0),
    opacity: 0.5,
  })

  //signature
  page.drawText(signature, {
    x: lineStartX7,
    y: bottomSectionYPos - labelValueSpacing * 2,
    size: fontSize,
    font,
    color: rgb(0, 0, 0),
  })
  page.drawLine({
    start: { x: iSignatureWidth, y: bottomSectionYPos - labelValueSpacing * 2 },
    end: { x: underlineEndX, y: bottomSectionYPos - labelValueSpacing * 2 },
    thickness: 1,
    color: rgb(0, 0, 0),
    opacity: 0.5,
  })

  //issue date
  page.drawText(issueDate, {
    x: lineStartX7,
    y: bottomSectionYPos - labelValueSpacing * 3,
    size: fontSize,
    font,
    color: rgb(0, 0, 0),
  })
  page.drawText(issueYear, {
    x: iDateWidth,
    y: bottomSectionYPos - labelValueSpacing * 3,
    size: fontSize,
    font: boldFont,
    color: rgb(0, 0, 0),
  })
  page.drawLine({
    start: { x: iYearWidth, y: bottomSectionYPos - labelValueSpacing * 3 },
    end: { x: iYearUnderlineEnd, y: bottomSectionYPos - labelValueSpacing * 3 },
    thickness: 1,
    color: rgb(0, 0, 0),
    opacity: 0.5,
  })
  page.drawText(issueYearLabel, {
    x: iYearUnderlineEnd,
    y: bottomSectionYPos - labelValueSpacing * 3,
    size: fontSize,
    font: boldFont,
    color: rgb(0, 0, 0),
  })
  page.drawLine({
    start: { x: iYearLabelEnd, y: bottomSectionYPos - labelValueSpacing * 3 },
    end: { x: iMonthDateEnd, y: bottomSectionYPos - labelValueSpacing * 3 },
    thickness: 1,
    color: rgb(0, 0, 0),
    opacity: 0.5,
  })
  page.drawLine({
    start: { x: iMonthDateEnd + 5, y: bottomSectionYPos - labelValueSpacing * 3 },
    end: { x: underlineEndX, y: bottomSectionYPos - labelValueSpacing * 3 },
    thickness: 1,
    color: rgb(0, 0, 0),
    opacity: 0.5,
  })

  //recipient
  //label starts
  const rLabelLeftPadding = vertical5LineX + 15

  //text widths
  const rNameWidth =
    rLabelLeftPadding + font.widthOfTextAtSize(recipientNameAndSurname, fontSize) + 5
  const rSignatureWidth = rLabelLeftPadding + font.widthOfTextAtSize(rSignature, fontSize) + 5
  const rDateWidth = rLabelLeftPadding + font.widthOfTextAtSize(recipientDate, fontSize) + 5
  const rYearWidth = rDateWidth + font.widthOfTextAtSize(recipientYear, fontSize)
  const rYearUnderlineEnd = rYearWidth + 15
  const rYearLabelEnd = rYearUnderlineEnd + font.widthOfTextAtSize(recipientYearLabel, fontSize)
  const rMonthDateEnd = rYearLabelEnd + 20

  //underline
  const underlineEndX2 = width - padding

  //name and surname
  page.drawText(recipientNameAndSurname, {
    x: rLabelLeftPadding,
    y: bottomSectionYPos - labelValueSpacing,
    size: fontSize,
    font,
    color: rgb(0, 0, 0),
  })
  page.drawLine({
    start: { x: rNameWidth, y: bottomSectionYPos - labelValueSpacing },
    end: { x: underlineEndX2, y: bottomSectionYPos - labelValueSpacing },
    thickness: 1,
    color: rgb(0, 0, 0),
    opacity: 0.5,
  })

  //signature
  page.drawText(rSignature, {
    x: rLabelLeftPadding,
    y: bottomSectionYPos - labelValueSpacing * 2,
    size: fontSize,
    font,
    color: rgb(0, 0, 0),
  })
  page.drawLine({
    start: { x: rSignatureWidth, y: bottomSectionYPos - labelValueSpacing * 2 },
    end: { x: underlineEndX2, y: bottomSectionYPos - labelValueSpacing * 2 },
    thickness: 1,
    color: rgb(0, 0, 0),
    opacity: 0.5,
  })

  //issue date
  page.drawText(recipientDate, {
    x: rLabelLeftPadding,
    y: bottomSectionYPos - labelValueSpacing * 3,
    size: fontSize,
    font,
    color: rgb(0, 0, 0),
  })
  page.drawText(recipientYear, {
    x: rDateWidth,
    y: bottomSectionYPos - labelValueSpacing * 3,
    size: fontSize,
    font: boldFont,
    color: rgb(0, 0, 0),
  })
  page.drawLine({
    start: { x: rYearWidth, y: bottomSectionYPos - labelValueSpacing * 3 },
    end: { x: rYearUnderlineEnd, y: bottomSectionYPos - labelValueSpacing * 3 },
    thickness: 1,
    color: rgb(0, 0, 0),
    opacity: 0.5,
  })
  page.drawText(recipientYearLabel, {
    x: rYearUnderlineEnd,
    y: bottomSectionYPos - labelValueSpacing * 3,
    size: fontSize,
    font: boldFont,
    color: rgb(0, 0, 0),
  })
  page.drawLine({
    start: { x: rYearLabelEnd, y: bottomSectionYPos - labelValueSpacing * 3 },
    end: { x: rMonthDateEnd, y: bottomSectionYPos - labelValueSpacing * 3 },
    thickness: 1,
    color: rgb(0, 0, 0),
    opacity: 0.5,
  })
  page.drawLine({
    start: { x: rMonthDateEnd + 5, y: bottomSectionYPos - labelValueSpacing * 3 },
    end: { x: underlineEndX2, y: bottomSectionYPos - labelValueSpacing * 3 },
    thickness: 1,
    color: rgb(0, 0, 0),
    opacity: 0.5,
  })

  //text widths
  //the end of pay until label
  const payUntilLabelEnd = lineStartX7 + font.widthOfTextAtSize(payUntilLabel, fontSize) + 5

  //pay until label
  page.drawText(payUntilLabel, {
    x: lineStartX7,
    y: bottomSectionYPos - labelValueSpacing * 4,
    size: fontSize,
    font,
    color: rgb(0, 0, 0),
  })

  page.drawText(payUntilDate, {
    x: payUntilLabelEnd,
    y: bottomSectionYPos - labelValueSpacing * 4,
    size: fontSize,
    font: italicFont,
    color: rgb(0, 0, 0),
  })

  drawTextWithLineBreaks(
    page,
    commentTextToDraw,
    lineEndX7 - 65,
    { x: lineStartX7, y: bottomSectionYPos - labelValueSpacing * 5 },
    10
  )

  // page.drawLine({
  //   start: { x: lineStartX7, y: lineStartY7 },
  //   end: { x: lineStartX7, y: bottomSectionPoint },
  //   thickness: 1,
  //   opacity: 0.5,
  //   color: rgb(0, 0, 0),
  // })

  const pdfBytes = await pdfDoc.save()
  // const blob = new Blob([pdfBytes], { type: "application/pdf" })
  // const url = URL.createObjectURL(blob)
  // window.open(url, "_blank")

  //invoice file name
  const fileName = data.docPrefix + formattedProformaNumber

  //path to files based on what type of file it is
  let pathToFile
  if (documentType === "invoice") {
    pathToFile = `invoices/${fileName}.pdf`
  } else {
    pathToFile = `proformas/${fileName}.pdf`
  }

  // Upload PDF to Firebase Storage
  const storageRef = ref(storage, pathToFile)
  await uploadBytes(storageRef, pdfBytes)

  // Get the download URL
  const downloadURL = await getDownloadURL(storageRef)

  return downloadURL
}
